import { Colors, ConfirmDialog, Icon, type OptionProps } from '@amelio/ui-library';
import { useState, useEffect, useMemo, useRef } from 'react';

import { Dashboard } from './components/Dashboard';
import { isLockableEvaluationAction, isReadOnlyManagerAssessment } from './evaluationsTier';
import { useLaunchEvaluationOptions } from './useLaunchEvaluationOptions';

import type { UserFilterParams } from '@/types/api';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type {
    CompanyAttribute as CompanyAttributeType,
    CompanyManager as CompanyManagerType,
} from '@/types/directory/Company';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { useCompetencyProfilesByEmployees } from '@/api/competency-services';
import {
    useCreateCycleInstance,
    useDeleteCycleInstance,
} from '@/api/cycle-evaluation-services/cycle-instance';
import { useGetCompanyInformation, useGetUsersByIds } from '@/api/directory';
import {
    useOngoingReviewsPaged,
    usePastReviewsPaged,
    useReadyToBeLaunchedReviewsPaged,
} from '@/api/review-services/review';
import { useSendReminder, useDeleteReviewInstance } from '@/api/review-services/review-instance';
import { CommonActions } from '@/constants/Actions';
import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { PerformanceProFeature } from '@/constants/PerformanceProFeature';
import { ReviewType } from '@/constants/review/CycleInstance';
import { Evaluation360Storage } from '@/constants/storage/Evaluation360Storage';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useCompanyAttributesFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import { usePerformanceTier } from '@/hooks/performance-tier/usePerformanceTier';
import { useProGate } from '@/hooks/performance-tier/useProGate';
import { useError } from '@/hooks/utils/useError';
import { evaluationDestination } from '@/navigation/evaluationDestination';
import { RouteName } from '@/navigation/types';
import { getReviewFormRouteName } from '@/navigation/utils';
import {
    clampToManagerAssessmentStep,
    FIRST_STEP as MANAGER_ASSESSMENT_FIRST_STEP,
    getManagerAssessmentStepPath,
} from '@/pages/ManagerAssessment/Evaluation/stepRoutes';
import { isManagerAssessmentSurveySubmitted } from '@/pages/ManagerAssessment/Evaluation/submissionState';
import { showSuccessToast } from '@/plugins/Toast';
import { getCompanyAttributeList } from '@/utils/company';
import {
    canCycleInstanceSendEmail,
    getAllCycleInstanceAssignedUserIds,
    getLastStepForReviewType,
    getPrepareLaunchLandingStep,
    getStepPath,
    isOngoingCycleInstance,
} from '@/utils/cycleInstance';
import { getLocalizedText } from '@/utils/localization';
import { lockReadOnlyActions } from '@/utils/lockReadOnlyActions';
import { requestPerformanceUpgrade } from '@/utils/requestPerformanceUpgrade';
import { getAllReviewInstanceAssignedUserIds } from '@/utils/reviewInstance';

const REVIEWS_TO_BE_LAUNCHED_PAGE_SIZE = 10;
const ONGOING_REVIEWS_PAGE_SIZE = 10;
const PAST_REVIEWS_PAGE_SIZE = 6;
const EXCLUDED_ATTRIBUTES: string[] = ['ageGroups', 'tenures'];

export const DashboardPage = () => {
    const { t, i18n } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const { isPro } = usePerformanceTier();
    const managerAssessmentGate = useProGate();

    const [cycleInstanceToDelete, setCycleInstanceToDelete] = useState<CycleInstance | undefined>(
        undefined,
    );
    const [reviewInstanceToDelete, setReviewInstanceToDelete] = useState<
        ReviewInstance | undefined
    >(undefined);
    const [reviewInstanceToRemind, setReviewInstanceToRemind] = useState<
        ReviewInstance | undefined
    >(undefined);

    const [reviewsToBeLaunchedPage, setReviewsToBeLaunchedPage] = useState(1);
    const [ongoingReviewsPage, setOngoingReviewsPage] = useState(1);
    const [pastReviewsPage, setPastReviewsPage] = useState(1);

    const isFirstRender = useRef(true);

    const { mutateAsync: sendReminder, isPending: sendReminderIsPending } = useSendReminder();
    const { mutateAsync: deleteCycleInstance, isPending: deleteCycleInstanceIsPending } =
        useDeleteCycleInstance();
    const { mutateAsync: deleteReviewInstance, isPending: deleteReviewInstanceIsPending } =
        useDeleteReviewInstance();

    const { data: companyInformation } = useGetCompanyInformation();

    const {
        selectedCompanyAttributes,
        filters,
        hasUserModified,
        onSelectCompanyAttribute,
        onSelectCompanyAttributeOption,
    } = useCompanyAttributesFilters({
        company: companyInformation,
        storageKey: Evaluation360Storage.DASHBOARD,
    });

    const companyAttributes: OptionProps[] = useMemo(() => {
        const attributes = getCompanyAttributeList(companyInformation);
        return Object.entries(attributes)
            .filter(([key]) => !EXCLUDED_ATTRIBUTES.includes(key))
            .map(([, attr]) => ({
                label: attr.name,
                value: attr.name,
            }));
    }, [companyInformation, i18n.language]);

    const filterParams: UserFilterParams = useMemo(() => {
        const toIds = (items: Array<CompanyAttributeType | CompanyManagerType>) =>
            items.map((i) => i.id).filter((id): id is string => !!id);

        const ifNotEmpty = (items: Array<CompanyAttributeType | CompanyManagerType>) =>
            items.length > 0 ? toIds(items) : undefined;

        return {
            departmentIds: ifNotEmpty(filters.departments),
            jobTitleIds: ifNotEmpty(filters.jobTitles),
            managerIds: ifNotEmpty(filters.managers),
            companyAddressIds: ifNotEmpty(filters.companyAddresses),
            employeeLevelIds: ifNotEmpty(filters.employeeLevels),
            customAttributeIds: ifNotEmpty(filters.customAttributes),
        };
    }, [filters]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (hasUserModified) {
            setReviewsToBeLaunchedPage(1);
            setOngoingReviewsPage(1);
            setPastReviewsPage(1);
        }
    }, [filterParams, hasUserModified]);

    const {
        data: reviewsToBeLaunchedData,
        isLoading: reviewsToBeLaunchedLoading,
        isRefetching: reviewsToBeLaunchedIsRefetching,
        error: reviewsToBeLaunchedError,
    } = useReadyToBeLaunchedReviewsPaged({
        pageNumber: reviewsToBeLaunchedPage,
        pageSize: REVIEWS_TO_BE_LAUNCHED_PAGE_SIZE,
        filters: filterParams,
    });

    const {
        data: ongoingReviewsData,
        isLoading: ongoingReviewsLoading,
        isRefetching: ongoingReviewsIsRefetching,
        error: ongoingReviewsError,
    } = useOngoingReviewsPaged({
        pageNumber: ongoingReviewsPage,
        pageSize: ONGOING_REVIEWS_PAGE_SIZE,
        filters: filterParams,
    });

    const {
        data: pastReviewsData,
        isLoading: pastReviewsLoading,
        isRefetching: pastReviewsIsRefetching,
        error: pastReviewsError,
    } = usePastReviewsPaged({
        pageNumber: pastReviewsPage,
        pageSize: PAST_REVIEWS_PAGE_SIZE,
        filters: filterParams,
    });

    const { mutateAsync: createCycleInstance, isPending: createCycleInstanceIsPending } =
        useCreateCycleInstance();

    // Manager Assessments stay listed short of Performance PRO, read-only (see `readOnlyLock`).
    const reviewsToBeLaunched = useMemo(
        () => reviewsToBeLaunchedData?.items ?? [],
        [reviewsToBeLaunchedData],
    );

    const ongoingReviews = useMemo(() => ongoingReviewsData?.items ?? [], [ongoingReviewsData]);

    const pastReviews = useMemo(() => pastReviewsData?.items ?? [], [pastReviewsData]);

    const revieweeIds = useMemo(() => {
        const ids = new Set<string>();

        reviewsToBeLaunched.forEach((cycleInstance) => {
            const assignments = cycleInstance.reviewAudience?.reviewAudienceAssignments ?? [];
            if (assignments.length === 1) {
                ids.add(assignments[0].assignedTo);
            }
        });

        pastReviews.forEach((reviewInstance) => {
            const revieweeId =
                reviewInstance.reviewAudienceAssignment?.assignedTo ?? reviewInstance.assignedTo;
            if (revieweeId) {
                ids.add(revieweeId);
            }
        });

        return Array.from(ids);
    }, [reviewsToBeLaunched, pastReviews]);

    // Competency profiles are Performance PRO: short of it, none is read and no card shows one.
    const profileQueries = useCompetencyProfilesByEmployees(isPro ? revieweeIds : []);

    const profileNameByUserId: Record<string, string> = {};
    revieweeIds.forEach((employeeId, index) => {
        const name = profileQueries[index]?.data?.name;
        if (name) {
            profileNameByUserId[employeeId] = getLocalizedText(name);
        }
    });

    const userIds: string[] = useMemo(() => {
        const reviewToBeLaunchedUserIds = getAllCycleInstanceAssignedUserIds(reviewsToBeLaunched);
        const ongoingReviewsUserIds = getAllReviewInstanceAssignedUserIds(ongoingReviews);
        const pastReviewsUserIds = getAllReviewInstanceAssignedUserIds(pastReviews);
        return [...reviewToBeLaunchedUserIds, ...ongoingReviewsUserIds, ...pastReviewsUserIds];
    }, [reviewsToBeLaunchedData, ongoingReviewsData, pastReviewsData]);

    const { data: users, isLoading: usersLoading } = useGetUsersByIds({
        userIds,
        identifier: 'dashboard',
    });

    const onNewReview = async (evaluationType: EvaluationTypeValue) => {
        if (evaluationType === EvaluationType.MANAGER_EVALUATION) {
            navigate({ path: RouteName.ManagerAssessment });

            return;
        }

        try {
            const response = await createCycleInstance(evaluationType);

            const routeName = getReviewFormRouteName(evaluationType);

            navigate({
                path: `${routeName}/${getStepPath(CycleInstanceSteps.SELECT_REVIEWEE)}/${response.id}`,
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const buttonOptions = useLaunchEvaluationOptions({
        onNewReview,
        isPending: createCycleInstanceIsPending,
    });

    const goToManagerAssessmentEvaluation = (cycleInstance: CycleInstance) => {
        // The assessed employee is the reviewee (like 360/self).
        const assesseeId = cycleInstance.reviewAudience?.reviewAudienceAssignments?.[0]?.assignedTo;

        // Land on the step the manager has reached (1=Evaluation, 2=Results, 3=Prepare Meeting,
        // 4=Meet), resolved through the single-source-of-truth step→route map so opening Prepare
        // Meeting is remembered rather than always falling back to Results.
        const hasSubmittedSurvey = isManagerAssessmentSurveySubmitted(cycleInstance);
        const reachedStep = hasSubmittedSurvey
            ? clampToManagerAssessmentStep(cycleInstance.managerAssessmentStep)
            : MANAGER_ASSESSMENT_FIRST_STEP;

        navigate({
            path: getManagerAssessmentStepPath(reachedStep, cycleInstance.id),
            state: {
                cycleInstanceId: cycleInstance.id,
                employeeId: assesseeId,
                competencyIds:
                    cycleInstance.assessmentCompetencies?.map((entry) => entry.competencyId) ?? [],
                isNewEvaluation: false,
                includeCompanyValues: cycleInstance.includeCompanyValues ?? false,
            },
        });
    };

    const onPrepareForLaunchClick = (cycleInstance: CycleInstance) => {
        // Manager assessment has its own review page (not the 360 audience/launch flow).
        if (cycleInstance.reviewType === EvaluationType.MANAGER_EVALUATION) {
            goToManagerAssessmentEvaluation(cycleInstance);

            return;
        }

        const routeName = getReviewFormRouteName(cycleInstance.reviewType);
        navigate({
            path: `${routeName}/${getStepPath(getPrepareLaunchLandingStep(cycleInstance))}/${cycleInstance.id}`,
        });
    };

    // Ongoing cycles already have all configuration steps completed; landing on the last step
    // (schedule-creation, the launch summary) matches what reviewers expect from "Edit", whereas
    // navigating to firstIncompleteStep can drop them mid-flow with no way to revisit prior steps.
    const onEditOngoingClick = (cycleInstance: CycleInstance) => {
        // Manager assessment has its own review page (not the 360 audience/launch flow).
        if (cycleInstance.reviewType === ReviewType.MANAGER_EVALUATION) {
            goToManagerAssessmentEvaluation(cycleInstance);

            return;
        }

        const routeName = getReviewFormRouteName(cycleInstance.reviewType);
        const lastStep = getLastStepForReviewType(cycleInstance.reviewType);
        navigate({
            path: `${routeName}/${getStepPath(lastStep)}/${cycleInstance.id}`,
        });
    };

    // Past (Completed) reviews are intentionally not handled here: the dashboard does not surface
    // an Edit menu for them, so onEditClick is only ever invoked from ToBeLaunched (Draft) and
    // Ongoing (Active / Launched) sections.
    const onEditClick = (cycleInstance?: CycleInstance) => {
        if (!cycleInstance) {
            return;
        }

        if (isOngoingCycleInstance(cycleInstance)) {
            onEditOngoingClick(cycleInstance);
            return;
        }

        onPrepareForLaunchClick(cycleInstance);
    };

    const onCancelSendReminders = () => {
        setReviewInstanceToRemind(undefined);
    };

    const onConfirmSendReminders = async () => {
        if (!reviewInstanceToRemind) {
            return;
        }

        try {
            await sendReminder(reviewInstanceToRemind);

            showSuccessToast({ message: t('evaluation360Dashboard.onSuccessSendReminders') });
        } catch (error) {
            handleError({ error });
        } finally {
            setReviewInstanceToRemind(undefined);
        }
    };

    const onViewResultsClick = (reviewInstance: ReviewInstance) => {
        const cycleInstance = reviewInstance.cycleInstance;
        // Manager assessments open their own pages (the manager fills them in), not the 360
        // results page. The performance profile's evaluation lists follow the same rule.
        const managerAssessment =
            cycleInstance?.reviewType === ReviewType.MANAGER_EVALUATION
                ? {
                      step: cycleInstance.managerAssessmentStep ?? 0,
                      isSurveySubmitted: isManagerAssessmentSurveySubmitted(cycleInstance),
                      // This list holds no meeting state, so the destination falls back to the
                      // reached step rather than jumping to the closing step.
                      isMeetingCompleted: false,
                  }
                : undefined;

        navigate({
            path: evaluationDestination({
                reviewInstanceId: reviewInstance.id,
                cycleInstanceId: cycleInstance?.id ?? '',
                managerAssessment,
            }),
        });
    };

    const onDeleteCycleInstanceClick = (cycleInstance?: CycleInstance) => {
        if (!cycleInstance) {
            return;
        }

        setCycleInstanceToDelete(cycleInstance);
    };

    const onDeleteReviewInstanceClick = (reviewInstance?: ReviewInstance) => {
        if (!reviewInstance) {
            return;
        }

        setReviewInstanceToDelete(reviewInstance);
    };

    const onConfirmDeleteCycleInstance = async () => {
        if (!cycleInstanceToDelete) {
            return;
        }

        try {
            await deleteCycleInstance(cycleInstanceToDelete.id);

            // Check if current page will be empty after deletion and navigate to previous page
            const currentItemCount = reviewsToBeLaunchedData?.itemCount ?? 0;
            const currentPageNumber = reviewsToBeLaunchedPage;

            if (currentItemCount === 1 && currentPageNumber > 1) {
                setReviewsToBeLaunchedPage(currentPageNumber - 1);
            }

            showSuccessToast({ message: t('evaluation360Dashboard.onSuccessDelete') });
        } catch (error) {
            handleError({ error });
        } finally {
            setCycleInstanceToDelete(undefined);
        }
    };

    const onConfirmDeleteReviewInstance = async () => {
        if (!reviewInstanceToDelete) {
            return;
        }

        try {
            await deleteReviewInstance(reviewInstanceToDelete.id);

            // Check if current page will be empty after deletion and navigate to previous page
            const currentItemCount = ongoingReviewsData?.itemCount ?? 0;
            const currentPageNumber = ongoingReviewsPage;

            if (currentItemCount === 1 && currentPageNumber > 1) {
                setOngoingReviewsPage(currentPageNumber - 1);
            }

            showSuccessToast({ message: t('evaluation360Dashboard.onSuccessDelete') });
        } catch (error) {
            handleError({ error });
        } finally {
            setReviewInstanceToDelete(undefined);
        }
    };

    // Manager Assessments stay listed short of Performance PRO, read-only: their Edit and Delete are
    // locked (choosing one offers the upgrade) for those shown locks, absent for everyone else.
    const readOnlyLock = (reviewType: string | undefined) => ({
        isReadOnly: isReadOnlyManagerAssessment(reviewType, isPro),
        gate: managerAssessmentGate,
        lockIcon: (
            <Icon
                name={'security-lock'}
                ariaLabel={t('performancePro.locked')}
            />
        ),
        onLockedClick: () => requestPerformanceUpgrade(PerformanceProFeature.ManagerAssessment),
        isLockable: isLockableEvaluationAction,
    });

    const reviewsToBeLaunchedActions = (cycleInstance: CycleInstance): OptionProps[] =>
        lockReadOnlyActions(
            reviewsToBeLaunchedActionsOf(cycleInstance),
            readOnlyLock(cycleInstance.reviewType),
        );

    const reviewsToBeLaunchedActionsOf = (cycleInstance: CycleInstance): OptionProps[] => [
        {
            id: CommonActions.EDIT,
            label: t('common.edit'),
            icon: <Icon name={'edit-edit-2'} />,
            onClick: () => onEditClick(cycleInstance),
        },
        {
            id: CommonActions.DELETE,
            label: t('common.delete'),
            icon: (
                <Icon
                    name={'essential-trash'}
                    color={Colors.ERROR}
                />
            ),
            danger: true,
            onClick: () => onDeleteCycleInstanceClick(cycleInstance),
        },
    ];

    const ongoingReviewsActions = (reviewInstance: ReviewInstance): OptionProps[] =>
        lockReadOnlyActions(
            ongoingReviewsActionsOf(reviewInstance),
            readOnlyLock(reviewInstance.cycleInstance?.reviewType),
        );

    const ongoingReviewsActionsOf = (reviewInstance: ReviewInstance): OptionProps[] =>
        [
            {
                id: CommonActions.EDIT,
                label: t('common.edit'),
                icon: <Icon name={'edit-edit-2'} />,
                onClick: () => onEditClick(reviewInstance?.cycleInstance),
            },
            {
                id: CommonActions.SEND_REMINDERS,
                label: t('evaluation360Dashboard.ongoingReviews.sendReminders'),
                icon: <Icon name={'arrow-refresh'} />,
                onClick: () => setReviewInstanceToRemind(reviewInstance),
            },
            {
                id: CommonActions.DELETE,
                label: t('common.delete'),
                icon: (
                    <Icon
                        name={'essential-trash'}
                        color={Colors.ERROR}
                    />
                ),
                danger: true,
                onClick: () => onDeleteReviewInstanceClick(reviewInstance),
            },
        ].filter(
            (action) =>
                action.id !== CommonActions.SEND_REMINDERS ||
                (reviewInstance.cycleInstance?.reviewType !== ReviewType.MANAGER_EVALUATION &&
                    canCycleInstanceSendEmail(reviewInstance.cycleInstance)),
        );

    useEffect(() => {
        if (reviewsToBeLaunchedError) {
            handleError({ error: reviewsToBeLaunchedError });
        }
        if (ongoingReviewsError) {
            handleError({ error: ongoingReviewsError });
        }
        if (pastReviewsError) {
            handleError({ error: pastReviewsError });
        }
    }, [reviewsToBeLaunchedError, ongoingReviewsError, pastReviewsError, handleError]);

    const getSendRemindersDialogContent = () => {
        // The server reminds every respondent who has not completed, which is what the list counts as
        // not yet answered. Only the number is shown: in an anonymous 360 the names are not ours to give.
        const pendingRespondentCount = reviewInstanceToRemind
            ? reviewInstanceToRemind.totalCount - reviewInstanceToRemind.completedResponseCount
            : 0;

        if (pendingRespondentCount === 0) {
            return {
                message: t('cycleEvaluationDashboard.sendReminders.emptyMessage'),
                confirmLabel: t('common.close'),
                onConfirm: onCancelSendReminders,
                hideCancel: true,
            };
        }

        return {
            message: t('evaluation360Dashboard.ongoingReviews.sendRemindersConfirm', {
                count: pendingRespondentCount,
            }),
            confirmLabel: t('common.confirm'),
            onConfirm: onConfirmSendReminders,
            hideCancel: false,
        };
    };

    const sendRemindersDialogContent = getSendRemindersDialogContent();

    return (
        <>
            <Dashboard
                reviewsToBeLaunched={reviewsToBeLaunched}
                reviewsToBeLaunchedLoading={
                    reviewsToBeLaunchedLoading ||
                    reviewsToBeLaunchedIsRefetching ||
                    deleteCycleInstanceIsPending ||
                    usersLoading
                }
                reviewsToBeLaunchedCurrentPage={reviewsToBeLaunchedPage}
                reviewsToBeLaunchedTotalCount={reviewsToBeLaunchedData?.totalCount ?? 0}
                reviewsToBeLaunchedTotalPages={Math.ceil(
                    (reviewsToBeLaunchedData?.totalCount ?? 0) / REVIEWS_TO_BE_LAUNCHED_PAGE_SIZE,
                )}
                onReviewsToBeLaunchedPageChange={setReviewsToBeLaunchedPage}
                ongoingReviews={ongoingReviews}
                ongoingReviewsLoading={
                    ongoingReviewsLoading ||
                    ongoingReviewsIsRefetching ||
                    deleteReviewInstanceIsPending ||
                    usersLoading
                }
                ongoingReviewsCurrentPage={ongoingReviewsPage}
                ongoingReviewsTotalCount={ongoingReviewsData?.totalCount ?? 0}
                ongoingReviewsTotalPages={Math.ceil(
                    (ongoingReviewsData?.totalCount ?? 0) / ONGOING_REVIEWS_PAGE_SIZE,
                )}
                onOngoingReviewsPageChange={setOngoingReviewsPage}
                pastReviews={pastReviews}
                pastReviewsLoading={pastReviewsLoading || pastReviewsIsRefetching || usersLoading}
                pastReviewsCurrentPage={pastReviewsPage}
                pastReviewsTotalCount={pastReviewsData?.totalCount ?? 0}
                pastReviewsTotalPages={Math.ceil(
                    (pastReviewsData?.totalCount ?? 0) / PAST_REVIEWS_PAGE_SIZE,
                )}
                onPastReviewsPageChange={setPastReviewsPage}
                createCycleInstanceIsPending={createCycleInstanceIsPending}
                buttonOptions={buttonOptions}
                users={users ?? []}
                profileNameByUserId={profileNameByUserId}
                ongoingReviewsActions={ongoingReviewsActions}
                reviewsToBeLaunchedActions={reviewsToBeLaunchedActions}
                onPrepareForLaunchClick={onPrepareForLaunchClick}
                onViewResults={onViewResultsClick}
                filterProps={{
                    companyAttributes,
                    selectedCompanyAttributes,
                    filters,
                    onSelectCompanyAttribute,
                    onSelectCompanyAttributeOption,
                }}
                hasActiveFilters={Object.values(filterParams).some((value) => value !== undefined)}
            />

            <ConfirmDialog
                isOpen={Boolean(cycleInstanceToDelete)}
                title={t('common.deleteConfirmTitle')}
                message={t('common.deleteConfirmMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={onConfirmDeleteCycleInstance}
                onCancel={() => setCycleInstanceToDelete(undefined)}
                isLoading={deleteCycleInstanceIsPending}
                isDestructive
            />

            <ConfirmDialog
                isOpen={Boolean(reviewInstanceToDelete)}
                title={t('common.deleteConfirmTitle')}
                message={t('common.deleteConfirmMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={onConfirmDeleteReviewInstance}
                onCancel={() => setReviewInstanceToDelete(undefined)}
                isLoading={deleteReviewInstanceIsPending}
                isDestructive
            />

            <ConfirmDialog
                isOpen={Boolean(reviewInstanceToRemind)}
                title={t('evaluation360Dashboard.ongoingReviews.sendReminders')}
                message={sendRemindersDialogContent.message}
                confirmLabel={sendRemindersDialogContent.confirmLabel}
                cancelLabel={t('common.cancel')}
                onConfirm={sendRemindersDialogContent.onConfirm}
                onCancel={onCancelSendReminders}
                isLoading={sendReminderIsPending}
                hideCancel={sendRemindersDialogContent.hideCancel}
            />
        </>
    );
};
